import React from "react";
import { Navigate } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetReferralsQuery } from "@/store/rest_api";
import { ReferralStatus } from "@/enum/ReferralStatus";
import { ReferralListItem } from "@/model/interface/Referral";
import { formatInvoiceDate } from "@/util/invoice";
import { formatMoneyAmount } from "@/util/sums";
import { getInitialsFromFullName } from "@/util/string";

const STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.PENDING]: "Signed up",
  [ReferralStatus.QUALIFIED]: "Credited",
  [ReferralStatus.BLOCKED]: "Not eligible",
};

const STATUS_PILLS: Record<ReferralStatus, string> = {
  [ReferralStatus.PENDING]: "status-pill-neutral",
  [ReferralStatus.QUALIFIED]: "status-pill-success",
  [ReferralStatus.BLOCKED]: "status-pill-destructive",
};

export default function ReferralsPage() {
  const { data: overview, isLoading, isError } = useGetReferralsQuery();

  const copyToClipboard = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Could not copy, select the text and copy it instead");
    }
  };

  const referralMeta = (referral: ReferralListItem) => {
    if (referral.status === ReferralStatus.QUALIFIED && referral.qualifiedAt) {
      return `Subscribed ${formatInvoiceDate(referral.qualifiedAt)}`;
    }
    return `Signed up ${formatInvoiceDate(referral.createdAt)}`;
  };

  if (isLoading) {
    return (
      <div className="p-4 text-foreground">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full max-w-2xl" />
      </div>
    );
  }

  if (isError || !overview?.enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const reward = formatMoneyAmount(String(overview.rewardAmount));
  const stats = [
    { label: "Signed up", value: String(overview.signedUp) },
    { label: "Paying", value: String(overview.paying) },
    { label: "Earned", value: formatMoneyAmount(String(overview.earned)) },
  ];

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Refer and earn</h1>
        <p className="text-muted-foreground text-sm">
          Give {reward}, get {reward}.
        </p>
      </div>

      <div className="max-w-2xl space-y-4">
        <div className="space-y-4 rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3 border-b pb-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {getInitialsFromFullName(overview.organisationName)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold">{overview.organisationName}</p>
              <p className="text-xs text-muted-foreground">Referral code and link</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Referral code</p>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold tracking-widest">{overview.code}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-full"
                title="Copy code"
                onClick={() => void copyToClipboard(overview.code ?? "", "Code copied")}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Referral link</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={overview.link ?? ""} />
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-full"
                title="Copy link"
                onClick={() => void copyToClipboard(overview.link ?? "", "Link copied")}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            When a business you refer subscribes to a plan, {overview.organisationName} and that business each get{" "}
            {reward} of credit on their prepaid balance for calls, texts and other usage.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border bg-card p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{stat.label}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="space-y-3 rounded-lg border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Referrals</p>
          {overview.referrals.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No referrals yet. Businesses that sign up through your link show up here.
            </p>
          ) : (
            <div className="divide-y">
              {overview.referrals.map((referral) => (
                <div key={referral.id} className="flex items-center gap-3 py-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {getInitialsFromFullName(referral.businessName)}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold">{referral.businessName ?? "Unnamed business"}</span>
                    <span className="text-xs text-muted-foreground">{referralMeta(referral)}</span>
                  </div>
                  <span className={STATUS_PILLS[referral.status]}>{STATUS_LABELS[referral.status]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
